// Soniox リアルタイム音声認識クライアント
// WebSocket + Web Audio API を使ってマイク音声を Soniox に送り、文字起こし結果を受け取る
//
// 使用例:
//   const client = new SonioxStreamingClient({
//     apiKey: "temp:xxx",
//     languageHints: ["ja"],
//     enableSpeakerDiarization: true,
//     context: { general: [{ key: "domain", value: "医療" }] },
//     onPartial: (text, speaker) => { /* 中間結果 */ },
//     onFinal: (text, speaker) => { /* 確定結果 */ },
//     onError: (error) => { /* エラー */ },
//   })
//   await client.start(mediaStream)
//   // ... 録音中 ...
//   await client.stop()

const WEBSOCKET_URL = "wss://stt-rt.jp.soniox.com/transcribe-websocket"
const DEFAULT_MODEL = "stt-rt-v5"
const SAMPLE_RATE = 16000
const SCRIPT_PROCESSOR_BUFFER_SIZE = 4096
// stop() 後に Soniox からの finished 通知 / 接続クローズを待つ最大時間（ms）
// これを超えたら強制的に停止扱いにして、ユーザーが待ち続けないようにする
const FINISH_WAIT_TIMEOUT_MS = 3000

export class SonioxStreamingClient {
  constructor({
    apiKey,
    model = DEFAULT_MODEL,
    languageHints = ["ja"],
    enableSpeakerDiarization = false,
    enableEndpointDetection = true,
    context = null,
    onPartial = null,
    onFinal = null,
    onEndpoint = null,
    onError = null,
  }) {
    this.apiKey = apiKey
    this.model = model
    this.languageHints = languageHints
    this.enableSpeakerDiarization = enableSpeakerDiarization
    this.enableEndpointDetection = enableEndpointDetection
    this.context = context
    this.onPartial = onPartial
    this.onFinal = onFinal
    this.onEndpoint = onEndpoint
    this.onError = onError

    this.ws = null
    this.audioContext = null
    this.source = null
    this.processor = null
    this.isRunning = false
    // stop() で finished 通知を待つための Promise の resolver
    this._finishedResolver = null
  }

  // 録音・送信開始（mediaStream は呼び出し元で取得・管理）
  async start(mediaStream) {
    if (this.isRunning) {
      throw new Error("[SonioxStreamingClient] Already running")
    }

    try {
      await this._setupWebSocket()
      this._setupAudioPipeline(mediaStream)
      this.isRunning = true
      console.log("[SonioxStreamingClient] Started")
    } catch (error) {
      this._handleError(error)
      throw error
    }
  }

  // 録音・送信停止
  // Soniox は end シグナル送信後に残りの partial を final に変換して送ってくるため、
  // `finished: true` の通知（または接続クローズ）を待ってから resolve する。
  // ref: https://soniox.com/docs/stt/rt/real-time-transcription
  async stop() {
    if (!this.isRunning) return

    this.isRunning = false

    // AudioContextを止めて音声送信を停止
    this._teardownAudioPipeline()

    // WebSocketに終了シグナル（空文字列）を送信
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send("")
      } catch (e) {
        console.warn("[SonioxStreamingClient] Failed to send end signal:", e)
      }
    }

    // Sonioxからの最終トークン + finished 通知を待つ
    await this._waitForFinished()

    console.log("[SonioxStreamingClient] Stopped")
  }

  // Sonioxからの「finished: true」通知 or 接続クローズを待つ。
  // タイムアウトを過ぎたら諦めて停止扱いにする（ネットワーク切断時の保険）。
  _waitForFinished() {
    // 接続がそもそも生きていなければ待つ必要なし
    if (!this.ws || this.ws.readyState === WebSocket.CLOSED) return Promise.resolve()

    return new Promise((resolve) => {
      const finish = () => {
        if (this._finishedResolver) {
          this._finishedResolver = null
          resolve()
        }
      }
      this._finishedResolver = finish
      setTimeout(finish, FINISH_WAIT_TIMEOUT_MS)
    })
  }

  // _handleMessage / onclose から呼ばれて、stop() を完了させる
  _notifyFinished() {
    if (this._finishedResolver) {
      this._finishedResolver()
    }
  }

  // --- private methods ---

  _setupWebSocket() {
    return new Promise((resolve, reject) => {
      this.ws = new WebSocket(WEBSOCKET_URL)
      this.ws.binaryType = "arraybuffer"

      this.ws.onopen = () => {
        const config = {
          api_key: this.apiKey,
          model: this.model,
          audio_format: "pcm_s16le",
          sample_rate: SAMPLE_RATE,
          num_channels: 1,
          language_hints: this.languageHints,
          enable_endpoint_detection: this.enableEndpointDetection,
          enable_speaker_diarization: this.enableSpeakerDiarization,
        }
        if (this.context) {
          config.context = this.context
        }
        this.ws.send(JSON.stringify(config))
        console.log("[SonioxStreamingClient] WebSocket connected, config sent")
        resolve()
      }

      this.ws.onmessage = (event) => this._handleMessage(event)
      this.ws.onerror = (event) => {
        const error = new Error("WebSocket error")
        console.error("[SonioxStreamingClient] WebSocket error:", event)
        this._handleError(error)
        reject(error)
      }
      this.ws.onclose = (event) => {
        console.log(`[SonioxStreamingClient] WebSocket closed: code=${event.code} reason=${event.reason}`)
        // finished 通知より先にクローズされた場合の保険
        this._notifyFinished()
      }
    })
  }

  _setupAudioPipeline(mediaStream) {
    // AudioContextを 16kHz で作成（Sonioxの要求サンプルレート）
    this.audioContext = new (window.AudioContext || window.webkitAudioContext)({
      sampleRate: SAMPLE_RATE,
    })

    this.source = this.audioContext.createMediaStreamSource(mediaStream)
    this.processor = this.audioContext.createScriptProcessor(
      SCRIPT_PROCESSOR_BUFFER_SIZE,
      1,
      1
    )

    this.processor.onaudioprocess = (event) => {
      if (!this.isRunning || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
        return
      }
      const float32Data = event.inputBuffer.getChannelData(0)
      const int16Data = this._convertFloat32ToInt16(float32Data)
      this.ws.send(int16Data.buffer)
    }

    this.source.connect(this.processor)
    this.processor.connect(this.audioContext.destination)
  }

  _teardownAudioPipeline() {
    if (this.processor) {
      this.processor.disconnect()
      this.processor.onaudioprocess = null
      this.processor = null
    }
    if (this.source) {
      this.source.disconnect()
      this.source = null
    }
    if (this.audioContext) {
      this.audioContext.close().catch((e) => {
        console.warn("[SonioxStreamingClient] Failed to close AudioContext:", e)
      })
      this.audioContext = null
    }
  }

  _handleMessage(event) {
    let data
    try {
      data = JSON.parse(event.data)
    } catch (e) {
      console.error("[SonioxStreamingClient] Failed to parse message:", e)
      return
    }

    if (data.error_code) {
      const error = new Error(`Soniox error ${data.error_code}: ${data.error_message}`)
      this._handleError(error)
      return
    }

    const tokens = data.tokens || []

    // <end> トークン（発話区切り）を検知して、通常テキストからは除外する
    // ref: https://soniox.com/docs/stt/rt/endpoint-detection
    const hasEndpoint = tokens.some((t) => t.text === "<end>")
    const speechTokens = tokens.filter((t) => t.text !== "<end>")

    // is_final ごとに集約してテキスト化
    const finalText = speechTokens.filter((t) => t.is_final).map((t) => t.text).join("")
    const partialText = speechTokens.filter((t) => !t.is_final).map((t) => t.text).join("")

    // speaker は最初の意味のあるトークンから取得（複数話者が混在する場合は呼び出し元で再整形）
    const speaker = speechTokens.length > 0 ? speechTokens[0].speaker : null

    if (finalText && this.onFinal) {
      this.onFinal(finalText, speaker)
    }
    if (partialText && this.onPartial) {
      this.onPartial(partialText, speaker)
    }
    if (hasEndpoint && this.onEndpoint) {
      this.onEndpoint()
    }

    // 全てのトークン処理が完了したことを伝える finished 通知
    if (data.finished === true) {
      this._notifyFinished()
    }
  }

  _handleError(error) {
    if (this.onError) {
      this.onError(error)
    } else {
      console.error("[SonioxStreamingClient] Error:", error)
    }
  }

  // Float32Array → Int16Array (PCM 16bit Little Endian) 変換
  _convertFloat32ToInt16(float32Array) {
    const int16Array = new Int16Array(float32Array.length)
    for (let i = 0; i < float32Array.length; i++) {
      const s = Math.max(-1, Math.min(1, float32Array[i]))
      int16Array[i] = s < 0 ? s * 0x8000 : s * 0x7fff
    }
    return int16Array
  }
}
